import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Paper, Divider, Alert, Grid, Card, CardContent, Chip, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const Guide = () => {
  const [activeSection, setActiveSection] = useState('supported-files');

  // 监听滚动位置
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['supported-files', 'step1', 'step2', 'step3'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 平滑滚动到指定位置
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // 简化的导航项配置
  const navigationItems = [
    {
      id: 'supported-files',
      label: '支持文件',
      icon: <InsertDriveFileIcon />
    },
    {
      id: 'step1',
      label: '第一步：文件预处理',
      icon: <EditIcon />
    },
    {
      id: 'step2', 
      label: '第二步：文件拆分',
      icon: <FolderIcon />
    },
    {
      id: 'step3',
      label: '第三步：拍摄规范', 
      icon: <CameraAltIcon />
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'rgba(228, 228, 228, 0.6)',
        py: 4
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* 左侧导航栏 */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: 'sticky',
                top: 100,
                zIndex: 10
              }}
            >
              <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    导航目录
                  </Typography>
                </Box>
                
                <List sx={{ p: 0 }}>
                  {navigationItems.map((item) => (
                    <ListItem key={item.id} disablePadding>
                      <ListItemButton
                        onClick={() => scrollToSection(item.id)}
                        sx={{
                          py: 2,
                          px: 3,
                          backgroundColor: activeSection === item.id ? 'rgba(67, 83, 255, 0.1)' : 'transparent',
                          borderLeft: activeSection === item.id ? '4px solid' : '4px solid transparent',
                          borderLeftColor: activeSection === item.id ? 'primary.main' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(67, 83, 255, 0.05)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: activeSection === item.id ? 'primary.main' : 'text.secondary',
                          minWidth: 40
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: activeSection === item.id ? 'primary.main' : 'text.primary'
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {item.title}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </Grid>

          {/* 主内容区域 */}
          <Grid item xs={12} md={9}>
            <Paper elevation={2} sx={{ borderRadius: 3 }}>
              {/* 页面标题 */}
              <Box sx={{ p: 4, pb: 0 }}>
                <Typography variant="h3" component="h1" sx={{ mb: 3, textAlign: 'center', fontWeight: 600, color: 'primary.main'}}>
                  翻译规范说明文档
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                  遵循正确的文件准备流程，显著提高AI翻译准确率
                </Typography>
              
                
                <Divider sx={{ mb: 4 }} />
              </Box>

              {/* 内容区域 */}
              <Box sx={{ p: 4, pt: 0, '& > *': { mb: 6 } }}>
                {/* 支持文件 */}
                <Box id="supported-files">
                  <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <InsertDriveFileIcon sx={{ mr: 2, fontSize: '2rem' }} />
                    支持文件类型
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}>
                    我们的翻译系统支持多种文档格式，涵盖个人、家庭、学历、企业等各类证件和文档。<span style={{ color: 'red', fontWeight: 'bold' }}>不支持的文件类型可能会翻译失败或质量不佳。</span>
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              个人证件
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              身份证、出生证明、工作证
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              驾驶证、行驶证、车辆登记证书
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              退休证
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <AccountBalanceIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              家庭证件
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              结婚证、离婚证
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              户口本、房产证
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <SchoolIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              学历证书
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              毕业证、学位证、成绩单
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              学生证、教师资格证
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <AccountBalanceIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              企业证件
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              营业执照、税务登记证
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              商标证书、开户许可证
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              流水清单
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              银行流水账单
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              个人所得税纳税清单
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              其它证明
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              在读证明、实习证明
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                              在职证明、工作证明、离职证明
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      <strong>支持格式：</strong>JPG、PNG、PDF文档等常见格式。建议上传清晰度较高的图片或文档以获得最佳翻译效果。
                    </Typography>
                  </Alert>
                </Box>

                {/* 第一步：文件预处理 */}
                <Box id="step1">
                  <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <Chip label="第一步" sx={{ mr: 2, bgcolor: 'primary.main', color: 'white', fontWeight: 600 }} />
                    文件预处理：移除干扰元素
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}>
                    在上传文件前，先对原始文件进行预处理，移除可能影响AI识别的干扰元素，确保文本内容清晰可读。
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '2px solid #4caf50' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                              推荐预处理操作
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3 }}>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>移除水印：</strong>去除背景水印、版权标识等遮挡文字的元素
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>清除印章：</strong>擦除或涂抹覆盖在文字上的红色印章、签名
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>去除手写批注：</strong>清理手写备注、涂改痕迹
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>优化对比度：</strong>调整亮度对比度，让文字更清晰
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>修正倾斜：</strong>旋转纠正文档，确保文字水平排列
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '2px solid #f44336' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                              影响翻译质量的问题
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3 }}>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>水印遮挡：</strong>背景水印与文字重叠，AI无法准确识别
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>印章覆盖：</strong>红色印章压盖在关键文字上
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>手写干扰：</strong>手写批注与打印文字混淆
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>对比度差：</strong>文字与背景颜色过于接近
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>文档倾斜：</strong>拍摄角度导致文字变形
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  <Alert severity="warning" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      <strong>重要提示：</strong>预处理时请保留所有原始文字内容，只移除视觉干扰元素。建议保存原始文件备份。
                    </Typography>
                  </Alert>
                </Box>

                {/* 第二步：文件拆分 */}
                <Box id="step2">
                  <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <Chip label="第二步" sx={{ mr: 2, bgcolor: 'primary.main', color: 'white', fontWeight: 600 }} />
                    文件拆分：确保一文件一证件
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}>
                    将不同类型的证件分别上传，避免在同一个文件中混合多种证件类型，确保AI能够准确识别和翻译每个证件的特定格式。
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '2px solid #4caf50' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                              正确的文件组织方式
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3 }}>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>身份证：</strong>正反面合并为一个PDF文件
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>护照：</strong>个人信息页单独一个文件
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>成绩单：</strong>每份成绩单独立保存
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>银行流水：</strong>每份流水分别保存
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>学历证书：</strong>毕业证、学位证分别保存
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>多页文档：</strong>按页码顺序合并为一个PDF
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', border: '2px solid #f44336' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                              错误的混合方式
                            </Typography>
                          </Box>
                          
                          <Box component="ul" sx={{ pl: 3 }}>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>证件混合：</strong>身份证、护照放在同一个文件中
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>类型混杂：</strong>成绩单与银行流水合并
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>时间混乱：</strong>不同月份的流水无序排列
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>格式不一：</strong>同一文档中既有扫描件又有照片
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>内容缺失：</strong>多页文档缺少中间页面
                            </Typography>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                              <strong>顺序错乱：</strong>双面证件正反面顺序颠倒
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                
                </Box>

                {/* 第三步：拍摄规范 */}
                <Box id="step3">
                  <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <Chip label="第三步" sx={{ mr: 2, bgcolor: 'primary.main', color: 'white', fontWeight: 600 }} />
                    拍摄规范：确保文件质量
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}>
                    高质量的拍摄是翻译准确的基础。不同类型的文件有其特定的拍摄要点，掌握正确方法能够显著提升翻译效果。
                  </Typography>

                  {/* 证件类型拍摄 */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                      <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      证件类型拍摄规范
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                      包括身份证、护照、驾驶证、户口本、结婚证、出生证明等个人证件。
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #4caf50' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                正确示例
                              </Typography>
                            </Box>
                            
                            {/* 图片占位区域 */}
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #4caf50'
                              }}
                            >
                              <img
                                src="/images/guide_license_correct.jpg"
                                alt="证件正确示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                正确示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                              核心要点：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>证件平铺，四角完整显示</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>光线均匀，无反光阴影</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>文字清晰，照片可辨识</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>正反面分别拍摄</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>垂直拍摄，无变形</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #f44336' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                                错误示例
                              </Typography>
                            </Box>
                            
                            {/* 图片占位区域 */}
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #f44336'
                              }}
                            >
                              <img
                                src="/images/guide_license_error.jpg"
                                alt="证件错误示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                错误示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                              常见问题：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>证件边缘被裁切</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>强烈反光遮挡文字</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>拍摄角度倾斜变形</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>手指或物体遮挡</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>光线不足模糊不清</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* 成绩单类型拍摄 */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                      <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      成绩单类型拍摄规范
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                      包括学历证明、成绩单、学位证书、毕业证书、在读证明等教育类文件。
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #4caf50' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                正确示例
                              </Typography>
                            </Box>
                            
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #4caf50'
                              }}
                            >
                              <img
                                src="/images/guide_score_correct.jpg"
                                alt="成绩单正确示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                正确示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                              核心要点：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>表格线条清晰完整</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>科目名称和成绩可读</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>页眉页脚信息完整</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>多页按顺序排列</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>学校印章清晰（建议预处理时移除）</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #f44336' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                                错误示例
                              </Typography>
                            </Box>
                            
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #f44336'
                              }}
                            >
                              <img
                                src="/images/guide_score_error.jpg"
                                alt="成绩单错误示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                错误示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                              常见问题：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>表格线条断裂模糊</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>成绩数字无法辨识</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>缺少关键页面</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>页面顺序混乱</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>印章覆盖关键信息</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* 银行流水类型拍摄 */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                      <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      银行流水类型拍摄规范
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                      包括银行对账单、存款证明、收入证明、工资单、税单等财务流水类文件。
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #4caf50' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                正确示例
                              </Typography>
                            </Box>
                            
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #4caf50'
                              }}
                            >
                              <img
                                src="/images/guide_bank_correct.jpg"
                                alt="银行流水正确示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                正确示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                              核心要点：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>银行抬头和日期清晰</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>交易明细完整显示</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>金额数字准确清晰</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>按月份时间顺序整理</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>银行印章清晰（建议预处理时保留）</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #f44336' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                                错误示例
                              </Typography>
                            </Box>
                            
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px dashed #f44336'
                              }}
                            >
                              <img
                                src="/images/guide_bank_error.jpg"
                                alt="银行流水错误示例"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                                错误示例图片
                              </Typography>
                            </Box>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                              常见问题：
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>银行名称或日期不清楚</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>金额数字模糊难辨</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>交易记录不完整</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>多月流水混乱无序</Typography>
                              <Typography component="li" variant="body1" sx={{ mb: 1 }}>表格格式严重变形</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* 常见问题 */}
                <Box sx={{ mt: 4, p: 3, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#f57c00', textAlign: 'center' }}>
                    常见问题及解决方案
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 如何移除文件中的水印？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 可以使用PDF编辑软件或在线工具去除水印，或使用图片编辑软件（如PS）处理图片文件。
                      </Typography>
                      
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 印章覆盖了重要文字怎么办？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 建议重新获取无印章的原始文件，或使用图片编辑工具小心移除印章，保留文字内容。
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 多个证件可以放在一个文件中吗？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 不建议。每种证件类型应单独上传，确保AI能准确识别各自的格式和内容。
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 翻译效果不理想怎么办？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 通常是文件质量问题，建议按照指南重新处理文件后再次上传。
                      </Typography>
                      
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 支持哪些文件格式？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 支持PDF、PNG、JPG格式，推荐使用PDF格式以保持最佳效果。
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Q: 文件大小有限制吗？</Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A: 单个文件最大支持50MB，如果超过请压缩文件或分页上传。
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* 联系客服 */}
                <Box sx={{ mt: 4, textAlign: 'center', p: 3, backgroundColor: 'rgba(67, 83, 255, 0.05)', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>需要专业指导？</Typography>
                  
                  {/* 二维码区域 */}
                  <Box
                    sx={{
                      width: 150,
                      height: 150,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <img
                      src="/images/support_qrcode.jpg"
                      alt="客服微信二维码"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                  </Box>

                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    扫码添加客服微信
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    专业客服为您提供一对一文件处理指导和翻译咨询
                  </Typography>


                  {/* 服务时间 */}
                  <Box sx={{ 
                    backgroundColor: 'rgba(67, 83, 255, 0.1)',
                    borderRadius: 2,
                    p: 2,
                    display: 'inline-block'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                      服务时间
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      周一至周日 9:00-21:00
                    </Typography>
                  </Box>
                </Box>

                {/* 版权信息 */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    © 2025 PageSmooth - 3步提升翻译质量，让每份文件都完美呈现
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Guide;